import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Switch, Button, message } from 'antd';
import { makePutCall } from '../../utils/helper';
import { getUserDetails } from '../../utils/common';

const { TextArea } = Input;

const EditCenter = ({ visible, center, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (center) {
      form.setFieldsValue({
        name: center.name,
        code: center.code,
        ip: center.ip,
        proxy_url: center.proxy_url || '',
        his_lab_url: center.his_lab_url || '',
        location: center.location || '',
        contact: center.contact || '',
        active: center.active,
      });
    }
  }, [center, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await makePutCall(`/centres/${center.id}`, {
        ...values,
      });

      if (response.data.success) {
        message.success('Center updated successfully');
        form.resetFields();
        onSuccess();
      } else {
        throw new Error(response.data.message || 'Failed to update center');
      }
    } catch (error) {
      message.error(error.message || 'Failed to update center');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title="Edit Center"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="admin-form"
      >
        <Form.Item
          label="Center Name"
          name="name"
          rules={[
            { required: true, message: 'Please enter center name' },
            { max: 100, message: 'Name must be less than 100 characters' },
          ]}
        >
          <Input placeholder="Enter center name" />
        </Form.Item>

        <Form.Item
          label="Center Code"
          name="code"
          rules={[
            { required: true, message: 'Please enter center code' },
            { max: 20, message: 'Code must be less than 20 characters' },
          ]}
        >
          <Input placeholder="Enter center code (e.g., CGH)" />
        </Form.Item>

        <Form.Item
          label="IP Address"
          name="ip"
          rules={[
            { required: true, message: 'Please enter IP address' },
            {
              pattern: /^(\d{1,3}\.){3}\d{1,3}$/,
              message: 'Please enter a valid IP address',
            },
          ]}
        >
          <Input placeholder="Enter IP address (e.g., 192.168.1.1)" />
        </Form.Item>

        <Form.Item
          label="Cache Proxy URL"
          name="proxy_url"
          tooltip="Base URL of this centre's cache proxy app, e.g. http://<centre-ip>:3333. Leave empty for centres without a local cache. Used for federated image loading."
          rules={[
            { max: 255, message: 'Proxy URL must be less than 255 characters' },
            {
              validator: (_, value) => {
                if (!value?.trim()) return Promise.resolve();
                try {
                  const url = new URL(value.trim());
                  if (url.protocol !== 'http:' && url.protocol !== 'https:') throw new Error();
                  return Promise.resolve();
                } catch {
                  return Promise.reject(new Error('Enter a valid http/https URL, e.g. http://192.168.1.50:3333'));
                }
              },
            },
          ]}
        >
          <Input placeholder="e.g. http://192.168.1.50:3333" />
        </Form.Item>

        <Form.Item
          label="HIS Lab URL"
          name="his_lab_url"
          tooltip="Centre's HIS lab-results portal, e.g. https://pmh-smjv2.yashodahospital.com:5944"
          rules={[
            { max: 255, message: 'HIS Lab URL must be less than 255 characters' },
            {
              validator: (_, value) => {
                if (!value?.trim()) return Promise.resolve();
                try {
                  const url = new URL(value.trim());
                  if (url.protocol !== 'http:' && url.protocol !== 'https:') throw new Error();
                  return Promise.resolve();
                } catch {
                  return Promise.reject(new Error('Enter a valid http/https URL'));
                }
              },
            },
          ]}
        >
          <Input placeholder="e.g. https://pmh-smjv2.yashodahospital.com:5944" />
        </Form.Item>

        <Form.Item
          label="Location"
          name="location"
          rules={[
            { max: 200, message: 'Location must be less than 200 characters' },
          ]}
        >
          <Input placeholder="Enter location" />
        </Form.Item>

        <Form.Item
          label="Contact"
          name="contact"
          rules={[
            { max: 100, message: 'Contact must be less than 100 characters' },
            { type: 'email', message: 'Please enter a valid email address' },
          ]}
        >
          <Input placeholder="Enter contact email" />
        </Form.Item>

        <Form.Item
          label="Active Status"
          name="active"
          valuePropName="checked"
        >
          <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
        </Form.Item>

        <Form.Item className="form-actions">
          <Button onClick={handleCancel} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            Update Center
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditCenter;
